import { axiosInstance } from "./AxiosInstance";

const API_URL = "/jobprofiles";
const JOBS_PAGE_CAP = 100;
const JOBS_DEFAULT_LIMIT = 10;

// --- Get all jobs with optional pagination and search ---
export const getJobs = async (page = 1, limit = 10, search = "") => {
  const safeLimit = Math.max(1, Math.min(JOBS_PAGE_CAP, Number.parseInt(limit, 10) || JOBS_DEFAULT_LIMIT));
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
  const safeSearch = search || "";
  
  const response = await axiosInstance.get(
    `${API_URL}?limit=${safeLimit}&page=${safePage}&search=${safeSearch}`
  );
  
  return {
    data: Array.isArray(response.data.data) ? response.data.data : [response.data.data],
    pagination: response.data.pagination || {},
    message: response.data.message,
  };
};

// --- Get jobs by status with pagination ---
export const getJobsByStatus = async (status, page = 1, limit = 10, search = "") => {
  const safeLimit = Math.max(1, Math.min(JOBS_PAGE_CAP, Number.parseInt(limit, 10) || JOBS_DEFAULT_LIMIT));
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
  const safeSearch = search || "";
  
  // `fields=summary`: the list rows only, no description or id lists --
  // see jobprofile.service.js. The Job Section loads a job whole to view it.
  const response = await axiosInstance.get(
    `${API_URL}?status=${status}&limit=${safeLimit}&page=${safePage}&search=${encodeURIComponent(safeSearch)}&fields=summary`
  );
  
  return {
    data: Array.isArray(response.data.data) ? response.data.data : [response.data.data],
    pagination: response.data.pagination || {},
    message: response.data.message,
  };
};

// --- Get jobs for a specific student with filters ---
export const getJobsForStudent = async (studentId, type = "active", page = 1, limit = 10, search = "") => {
  const safeLimit = Math.max(1, Math.min(JOBS_PAGE_CAP, Number.parseInt(limit, 10) || JOBS_DEFAULT_LIMIT));
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
  const safeSearch = search || "";
  
  // Summary rows, as in getJobsByStatus.
  let url = `${API_URL}?limit=${safeLimit}&page=${safePage}&search=${encodeURIComponent(safeSearch)}&fields=summary`;
  
  if (type === "active") {
    url += `&active=true`;
  } else if (type === "applied") {
    url += `&applied=true`;
  } else if (type === "missed") {
    url += `&missed=true`;
  }
  
  // Add student identifier
  if (typeof studentId === 'string' && studentId.includes('@')) {
    url += `&email=${encodeURIComponent(studentId)}`;
  } else {
    url += `&studentId=${studentId}`;
  }
  
  const response = await axiosInstance.get(url);
  
  return {
    data: Array.isArray(response.data.data) ? response.data.data : [response.data.data],
    pagination: response.data.pagination || {},
    message: response.data.message,
  };
};

// --- Get applied jobs for a student ---
export const getAppliedJobs = async (studentId, page = 1, limit = 10, search = "") => {
  const safeLimit = Math.max(1, Math.min(JOBS_PAGE_CAP, Number.parseInt(limit, 10) || JOBS_DEFAULT_LIMIT));
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
  const safeSearch = search || "";
  
  let url = `${API_URL}?limit=${safeLimit}&page=${safePage}&search=${encodeURIComponent(safeSearch)}&applied=true&fields=summary`;
  
  if (typeof studentId === 'string' && studentId.includes('@')) {
    url += `&email=${encodeURIComponent(studentId)}`;
  } else {
    url += `&studentId=${studentId}`;
  }
  
  const response = await axiosInstance.get(url);
  
  return {
    data: Array.isArray(response.data.data) ? response.data.data : [response.data.data],
    pagination: response.data.pagination || {},
    message: response.data.message,
  };
};

// --- Get missed jobs for a student ---
export const getMissedJobs = async (studentId, page = 1, limit = 10, search = "") => {
  const safeLimit = Math.max(1, Math.min(JOBS_PAGE_CAP, Number.parseInt(limit, 10) || JOBS_DEFAULT_LIMIT));
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
  const safeSearch = search || "";
  
  let url = `${API_URL}?limit=${safeLimit}&page=${safePage}&search=${encodeURIComponent(safeSearch)}&missed=true&fields=summary`;
  
  if (typeof studentId === 'string' && studentId.includes('@')) {
    url += `&email=${encodeURIComponent(studentId)}`;
  } else {
    url += `&studentId=${studentId}`;
  }
  
  const response = await axiosInstance.get(url);
  
  return {
    data: Array.isArray(response.data.data) ? response.data.data : [response.data.data],
    pagination: response.data.pagination || {},
    message: response.data.message,
  };
};

// --- Create a new job ---
export const createJob = async (jobData) => {
  const response = await axiosInstance.post(API_URL, jobData);
  return response.data;
};

// --- Update a job by ID ---
export const updateJob = async (jobId, jobData) => {
  const payload = {
    ...jobData,
    createdBy: jobData.updatedBy || "Admin"
  };
  
  const response = await axiosInstance.put(`${API_URL}/${jobId}`, payload);
  return response.data;
};

// --- Delete a job by ID ---
export const deleteJob = async (jobId) => {
  const response = await axiosInstance.delete(`${API_URL}/${jobId}`);
  return response.data;
};

// --- Get a job by ID ---
export const getJobById = async (jobId) => {
  const response = await axiosInstance.get(`${API_URL}/${jobId}`);
  return {
    data: response.data.data,
    status: response.status,
    message: response.data.message,
  };
};

// services/JobApplicationService.js (add this new function)

export const getStudentApplicationInfo = async (email) => {
  try {
    const response = await axiosInstance.get(
      `${API_URL}/application-info?email=${email}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching student application info:", error);
    throw error;
  }
};